import React, { useState, useEffect, useMemo } from 'react';
import { Layout, Menu, Button, Dropdown, Avatar, Space, Typography, Badge, Drawer, Tooltip, Tag } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    CloseOutlined,
    GlobalOutlined,
    SettingOutlined,
    UserOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    BellOutlined,
    LogoutOutlined,
    TeamOutlined,
    MenuOutlined,
    ShopOutlined,
    FileTextOutlined,
    SafetyOutlined,
    CloudServerOutlined,
    HddOutlined,
    SunOutlined,
    MoonOutlined,
    ThunderboltOutlined,
    MailOutlined,
    DashboardOutlined,
    ApartmentOutlined,
    IdcardOutlined,
    ContainerOutlined,
    SendOutlined,
    RocketOutlined,
    ClusterOutlined,
    CloudOutlined,
    CustomerServiceOutlined,
    SearchOutlined,
    HistoryOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../shared/hooks/useAuth';
import { useTheme } from '../../theme/ThemeContext';

import { adminPath } from '../../shared/config/paths';
import ErrorBoundary from '../../shared/components/ErrorBoundary';
import systemService from '../services/systemService';
import supportAdminService from '../services/supportAdminService';
import { t as i18nT } from '../../shared/i18n';
import { ADMIN_MENU, flattenMenu } from './adminMenu';
import CommandPalette from '../components/CommandPalette';

const { Header, Sider, Content } = Layout;
const { Title } = Typography;

// 取菜单项 label 里 <Link to="..."> 的目标路径
const linkTarget = (label) => {
    const to = label && label.props ? label.props.to : null;
    return typeof to === 'string' ? to.split('?')[0].replace(/\/+$/, '') || '/' : null;
};

/**
 * 根据当前路径计算菜单 selectedKeys / openKeys。
 * 规则：叶子 Link 路径是当前路径的前缀（按 "/" 段边界）时命中，取最长的那个；
 * 详情页 /cdn/domains/1 → 命中 /cdn/domains。都不命中时（孤儿路由，如 /cdn/workers），
 * 只展开首段路径相同的那个分组，不乱选。
 */
// 不在菜单里的详情/旧路由 → 归到对应菜单项高亮（按段前缀匹配）
const MENU_ALIASES = [
    ['/dns/records', '/dns/zones'],
    ['/domain-registration/detail', '/domain-registration/domains'],
    ['/domain-registration/users', '/domain-registration/domains'],
    ['/system/legacy-settings', '/system/settings'],
    ['/analytics', '/dns/analytics'],
];

const RECENT_KEY = 'adminRecentPages';
const RECENT_MAX = 6;
const MENU_LEAVES = flattenMenu(ADMIN_MENU);

const resolveMenuState = (items, pathname) => {
    let path = (pathname || '/').replace(/\/+$/, '') || '/';
    const alias = MENU_ALIASES.find(([from]) => path === from || path.startsWith(from + '/'));
    if (alias) path = alias[1];
    let best = null;
    const groupByFirstSeg = {};
    const walk = (list, ancestors) => {
        (list || []).forEach((item) => {
            if (!item) return;
            if (item.children) {
                // type:'group' 只是组内小标题,不是可展开的子菜单
                walk(item.children, item.type === 'group' ? ancestors : [...ancestors, item.key]);
                return;
            }
            const to = linkTarget(item.label);
            if (!to) return;
            const seg = to.split('/').filter(Boolean)[0];
            if (seg && ancestors.length && !groupByFirstSeg[seg]) groupByFirstSeg[seg] = ancestors;
            const hit = path === to || (to !== '/' && path.startsWith(to + '/'));
            if (hit && (!best || to.length > best.to.length)) {
                best = { key: item.key, to, ancestors };
            }
        });
    };
    walk(items, []);
    if (best) return { selectedKeys: [best.key], openKeys: best.ancestors };
    const seg = path.split('/').filter(Boolean)[0];
    return { selectedKeys: [], openKeys: (seg && groupByFirstSeg[seg]) || [] };
};

const AdminLayout = ({ children }) => {
    // 获取窗口宽度来确定是否是移动设备
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    // 从localStorage获取折叠状态，默认为折叠状态
    const [collapsed, setCollapsed] = useState(localStorage.getItem('adminSidebarCollapsed') === 'true');
    // 新增抽屉菜单状态
    const [drawerVisible, setDrawerVisible] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();
    const { currentUser, logout } = useAuth();
    const { isDark, toggleTheme } = useTheme();

    // 监听窗口尺寸变化
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // 判断是否是移动设备
    const isMobile = windowWidth < 768;

    // 菜单选中/展开：从菜单树里每个 <Link to> 反查当前路径（最长前缀、按段边界匹配），
    // 不再假设 /admin 前缀 —— 分开部署时 ADMIN_BASE_PATH='' 。见 resolveMenuState。
    const [openKeys, setOpenKeys] = useState([]);

    // 切换折叠状态
    const toggleCollapsed = () => {
        if (isMobile) {
            // 在移动端，控制抽屉菜单
            setDrawerVisible(!drawerVisible);
        } else {
            // 在桌面端，控制侧边栏折叠
            const newCollapsed = !collapsed;
            setCollapsed(newCollapsed);
            localStorage.setItem('adminSidebarCollapsed', newCollapsed.toString());
        }
    };

    const handleLogout = async () => {
        await logout();
    };

    const userMenu = (
        <Menu>
            <Menu.Item key="profile" icon={<UserOutlined />}>
                <Link to={adminPath("/settings")}>个人资料</Link>
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={handleLogout}>
                退出登录
            </Menu.Item>
        </Menu>
    );

    // L2 调度是否启用（env CDN_L2_TOPOLOGY_ENABLED，只读）；关闭时菜单里标「未启用」
    const [l2Off, setL2Off] = useState(false);
    useEffect(() => {
        let alive = true;
        systemService.getRuntimeSettings()
            .then((r) => { if (alive && r && r.success) setL2Off(!r.data?.CDN_L2_TOPOLOGY_ENABLED?.value); })
            .catch(() => {});
        return () => { alive = false; };
    }, []);

    // 待处理工单数（菜单角标）：切页时刷新
    const [openTickets, setOpenTickets] = useState(0);
    useEffect(() => {
        let alive = true;
        supportAdminService.getTickets({ status: 'open', per_page: 1 })
            .then((r) => { if (alive && r && r.status === 'success') setOpenTickets(Number(r.data?.counts?.open) || 0); })
            .catch(() => {});
        return () => { alive = false; };
    }, [location.pathname]);

    // 菜单定义在 ./adminMenu.js（2026-10-26 按产品重组，最多两级；组内用小标题分段）。
    const MENU_ICONS = {
        dashboard: <DashboardOutlined />, team: <TeamOutlined />, support: <CustomerServiceOutlined />,
        global: <GlobalOutlined />, apartment: <ApartmentOutlined />, cloudServer: <CloudServerOutlined />,
        container: <ContainerOutlined />, hdd: <HddOutlined />, shop: <ShopOutlined />, idcard: <IdcardOutlined />,
        safety: <SafetyOutlined />, mail: <MailOutlined />, send: <SendOutlined />, cloud: <CloudOutlined />,
        rocket: <RocketOutlined />, thunderbolt: <ThunderboltOutlined />, cluster: <ClusterOutlined />,
        file: <FileTextOutlined />, setting: <SettingOutlined />,
    };
    const L = (path, text) => <Link to={adminPath(path)}>{text}</Link>;
    const leafLabel = (it) => {
        const text = it.tKey ? i18nT(it.zh) : it.zh;
        if (it.badge === 'tickets') return <>{text}{openTickets > 0 && <Badge count={openTickets} size="small" style={{ marginLeft: 6 }} />}</>;
        if (it.badge === 'l2') return <>{text}{l2Off && <Tag style={{ marginLeft: 6 }}>未启用</Tag>}</>;
        return text;
    };
    const toItem = (it) => {
        if (it.section) {
            return { key: `sec:${it.section}:${it.items[0].path}`, type: 'group', label: it.section, children: it.items.map(toItem) };
        }
        if (it.items) {
            return { key: it.key, icon: MENU_ICONS[it.icon], label: it.zh, children: it.items.map(toItem) };
        }
        return { key: it.path, icon: it.icon ? MENU_ICONS[it.icon] : undefined, label: L(it.path, leafLabel(it)) };
    };
    const menuItems = ADMIN_MENU.map(toItem);

    const { selectedKeys: menuSelectedKeys, openKeys: routeOpenKeys } = resolveMenuState(menuItems, location.pathname);
    const getSelectedKeys = () => menuSelectedKeys;
    const routeOpenKeysSig = routeOpenKeys.join('|');

    // 路由变化时自动展开当前页所在的分组（保留用户手动展开的其它分组）
    useEffect(() => {
        if (!routeOpenKeysSig) return;
        setOpenKeys((prev) => Array.from(new Set([...prev, ...routeOpenKeysSig.split('|')])));
    }, [routeOpenKeysSig]);

    const menuOpenProps = { openKeys, onOpenChange: setOpenKeys };

    // 最近访问（localStorage，只存本浏览器；读写失败就当没有）
    const menuSelectedKey = menuSelectedKeys[0] || '';
    const [recent, setRecent] = useState(() => {
        try {
            const v = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
            return Array.isArray(v) ? v.filter((r) => r && typeof r.path === 'string' && typeof r.label === 'string').slice(0, RECENT_MAX) : [];
        } catch (e) {
            return [];
        }
    });
    useEffect(() => {
        const leafItem = MENU_LEAVES.find((l) => l.path === menuSelectedKey);
        if (!leafItem || leafItem.path === '/dashboard') return;
        const path = (location.pathname || '/').replace(/\/+$/, '') || '/';
        const base = leafItem.tKey ? i18nT(leafItem.zh) : leafItem.zh;
        const rest = path.startsWith(adminPath(leafItem.path) + '/') ? path.slice(adminPath(leafItem.path).length + 1).split('/')[0] : '';
        const label = rest ? `${base} · ${rest}` : base;
        setRecent((prev) => {
            const next = [{ path: path + (location.search || ''), label }, ...prev.filter((r) => r.label !== label)].slice(0, RECENT_MAX);
            try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch (e) { /* 隐私模式等 */ }
            return next;
        });
    }, [location.pathname, location.search, menuSelectedKey]);

    const recentGroup = recent.length ? [{
        key: 'g-recent', icon: <HistoryOutlined />, label: '最近访问',
        children: recent.map((r) => ({ key: `recent:${r.path}`, label: <Link to={r.path}>{r.label}</Link> })),
    }] : [];
    const sideMenuItems = [...recentGroup, ...menuItems];

    // 命令面板：Ctrl/⌘+K 或顶栏搜索框
    const [paletteOpen, setPaletteOpen] = useState(false);
    useEffect(() => {
        const onKey = (e) => {
            if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
                e.preventDefault();
                setPaletteOpen((v) => !v);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);
    // 面板里的“最近访问”存的是完整路径，面板会再拼 adminPath，这里先去掉前缀
    const paletteRecent = useMemo(() => recent.map((r) => ({
        label: r.label,
        path: adminPath('/') !== '/' && r.path.startsWith(adminPath('/')) ? r.path.slice(adminPath('/').length - 1) : r.path,
    })), [recent]);

    return (
        <Layout style={{ minHeight: '100vh' }}>
            {/* 桌面端侧边栏 */}
            {!isMobile && (
                <Sider
                    trigger={null}
                    collapsible
                    collapsed={collapsed}
                    breakpoint="lg"
                    theme="dark"
                    width={256}
                    collapsedWidth={80}
                    style={{
                        overflow: 'auto',
                        height: '100vh',
                        position: 'sticky',
                        top: 0,
                        left: 0,
                        zIndex: 1000,
                        background: isDark ? '#1a1a2e' : '#001529'
                    }}
                >
                    <div className="logo" style={{
                        height: '64px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        padding: collapsed ? '0' : '0 16px',
                        borderBottom: '1px solid rgba(255,255,255,0.1)'
                    }}>
                        <Title level={collapsed ? 5 : 4} style={{ margin: 0, whiteSpace: 'nowrap' }} className="gradient-text">
                            {collapsed ? 'Yeap' : 'Yeapcloud Admin'}
                        </Title>
                    </div>
                    <Menu
                        theme="dark"
                        mode="inline"
                        selectedKeys={getSelectedKeys()}
                        {...(collapsed ? {} : menuOpenProps)}
                        items={sideMenuItems}
                        style={{ background: 'transparent' }}
                    />
                </Sider>
            )}

            {/* 移动端抽屉菜单 */}
            {isMobile && (
                <Drawer
                    title={<span className="gradient-text">YeapCloud Admin</span>}
                    placement="left"
                    onClose={() => setDrawerVisible(false)}
                    open={drawerVisible}
                    styles={{
                        content: { background: isDark ? '#1a1a2e' : '#001529' },
                        header: { borderBottom: '1px solid rgba(255,255,255,0.1)' },
                        body: { padding: 0 },
                    }}
                    closeIcon={<CloseOutlined style={{ color: 'rgba(255,255,255,0.65)' }} />}
                    width={250}
                    style={{ zIndex: 1001 }}
                    className="mobile-drawer"
                >
                    <Menu
                        theme="dark"
                        mode="inline"
                        selectedKeys={getSelectedKeys()}
                        {...menuOpenProps}
                        items={sideMenuItems}
                        onClick={() => setDrawerVisible(false)}
                        style={{ background: 'transparent' }}
                    />
                </Drawer>
            )}

            <Layout>
                <Header
                    className="glass-header"
                    style={{
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        position: 'sticky',
                        top: 0,
                        zIndex: 999,
                    }}
                >
                    <Button
                        type="text"
                        icon={isMobile ? <MenuOutlined /> : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)}
                        onClick={toggleCollapsed}
                        style={{ fontSize: '16px', width: 64, height: 64 }}
                    />

                    {isMobile && (
                        <div style={{ flex: 1, textAlign: 'center' }}>
                            <Title level={5} style={{ margin: 0 }}>管理后台</Title>
                        </div>
                    )}

                    <Space size="middle" style={{ marginRight: 16 }}>
                        {isMobile ? (
                            <Button shape="circle" icon={<SearchOutlined />} onClick={() => setPaletteOpen(true)} />
                        ) : (
                            <Button onClick={() => setPaletteOpen(true)} style={{ width: 260, textAlign: 'left', color: 'var(--ant-color-text-tertiary, #999)' }}>
                                <SearchOutlined /> 搜索页面 / 用户 / 域名 / IP
                                <span style={{ float: 'right', fontSize: 12, opacity: 0.7 }}>{/Mac|iPhone|iPad/.test(navigator.platform || '') ? '⌘K' : 'Ctrl K'}</span>
                            </Button>
                        )}
                        <Tooltip title="待处理工单">
                            <Badge count={openTickets} size="small">
                                <Button shape="circle" icon={<BellOutlined />} onClick={() => navigate(adminPath('/support/tickets?status=open'))} />
                            </Badge>
                        </Tooltip>
                        <Tooltip title={isDark ? '切换到亮色模式' : '切换到暗色模式'}>
                            <Button
                                shape="circle"
                                icon={isDark ? <SunOutlined /> : <MoonOutlined />}
                                onClick={toggleTheme}
                                className="theme-toggle-btn"
                            />
                        </Tooltip>
                        <Dropdown overlay={userMenu} trigger={['click']}>
                            <Avatar
                                icon={<UserOutlined />}
                                style={{ cursor: 'pointer' }}
                                className="avatar-bordered"
                            />
                        </Dropdown>
                    </Space>
                </Header>
                <Content
                    className="page-content modern-card"
                    style={{
                        margin: isMobile ? '8px' : '24px 16px',
                        padding: isMobile ? '16px' : 24,
                        minHeight: 280,
                        overflow: 'auto'
                    }}
                >
                    <ErrorBoundary resetKeys={[location.pathname]}>{children}</ErrorBoundary>
                </Content>
            </Layout>
            <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} recent={paletteRecent} />
        </Layout>
    );
};

export default AdminLayout;
